import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'
import { getRuntimeVariable } from './getRuntimeVariable'

declare const window: {
  __ENV?: any
}

const boolean = z.enum(['true', 'false']).transform((value) => value === 'true')

const baseEnv = {
  server: {
    APP_ENV: z.enum(['dev', 'halfcall', 'halfdone', 'uereport']).optional(),
    APP_NAME: z.string().min(1),
    DATABASE_URL: z.string().url().refine((url) => url.startsWith('postgres') || url.startsWith('mysql')),
    ENCRYPTION_SECRET: z.string().length(32),
    NEXTAUTH_SECRET: z.string().min(1),
    NEXTAUTH_URL: z.string().url(),
    DOMAIN: z.string().min(1),
    BACKEND_API: z.string().min(1),
    DISABLE_SIGNUP: boolean.optional().default('false'),
    ADMIN_EMAIL: z.string().email().optional(),
    DEBUG: boolean.optional().default('false'),
    AGENT_URL: z.string().url(),
    PBX_URL: z.string().url(),
    PBX_TOKEN: z.string().min(1),
  },
  client: {
    NEXT_PUBLIC_API_URL: z.string().optional(),
    NEXT_PUBLIC_LOGIN_URL: z.string().optional(),
    NEXT_PUBLIC_RETURN_URL: z.string().optional(),
    NEXT_PUBLIC_EDIT_GRAPH_MODE: boolean.optional(),
    NEXT_PUBLIC_WECHAT_APPID_HW: z.string().optional(),
  },
  runtimeEnv: {
    NEXT_PUBLIC_API_URL: getRuntimeVariable('NEXT_PUBLIC_API_URL'),
    NEXT_PUBLIC_LOGIN_URL: getRuntimeVariable('NEXT_PUBLIC_LOGIN_URL'),
    NEXT_PUBLIC_RETURN_URL: getRuntimeVariable('NEXT_PUBLIC_RETURN_URL'),
    NEXT_PUBLIC_EDIT_GRAPH_MODE: getRuntimeVariable('NEXT_PUBLIC_EDIT_GRAPH_MODE'),
    NEXT_PUBLIC_WECHAT_APPID_HW: getRuntimeVariable('NEXT_PUBLIC_WECHAT_APPID_HW'),
  },
}

const smtpEnv = {
  server: {
    SMTP_USERNAME: z.string().min(1).optional(),
    SMTP_PASSWORD: z.string().min(1).optional(),
    SMTP_HOST: z.string().min(1).optional(),
    SMTP_PORT: z.coerce.number().optional().default(25),
    SMTP_AUTH_DISABLED: boolean.optional().default('false'),
    SMTP_SECURE: boolean.optional().default('false'),
  },
  client: {
    NEXT_PUBLIC_SMTP_FROM: z.string().min(1).optional(),
  },
  runtimeEnv: {
    NEXT_PUBLIC_SMTP_FROM: getRuntimeVariable('NEXT_PUBLIC_SMTP_FROM'),
  },
}

const s3Env = {
  server: {
    S3_ACCESS_KEY: z.string().min(1).optional(),
    S3_SECRET_KEY: z.string().min(1).optional(),
    S3_BUCKET: z.string().min(1).optional().default('stock'),
    S3_PORT: z.coerce.number().optional(),
    S3_ENDPOINT: z.string().min(1).optional(),
    S3_SSL: boolean.optional().default('true'),
    S3_REGION: z.string().min(1).optional(),
    S3_PUBLIC_CUSTOM_DOMAIN: z.string().url().optional(),
  },
}

// 新增OSS配置
const ossEnv = {
  server: {
    OSS_APPKEY: z.string().min(1),
    OSS_SECRECTKEY: z.string().min(1),
    OSS_REGION: z.string().min(1),
    OSS_BUCKET: z.string().min(1),
    OSS_ENDPOINT: z.string().min(1),
    OSS_CDN: z.string().min(1),
  },
}

const redisEmv = {
  server:{
    UPSTASH_REDIS_REST_URL: z.string().optional(),
    UPSTASH_REDIS_REST_TOKEN: z.string().optional(),
  }
}

// 新增阿里云短信配置
const alismsEnv = {
  server: {
    ALISMS_ACCESS_KEY_ID: z.string().min(1),
    ALISMS_ACCESS_KEY_SECRET: z.string().min(1),
    ALISMS_ENDPOINT: z.string().min(1),
    ALISMS_TEMPLATE_CODE: z.string().min(1),
    ALISMS_SIGN: z.string().min(1),
    ALISMS_CODE: z.string().min(1),
  },
}

// 新增Livekit配置
const livekitEnv = {
  server: {
    LIVEKIT_API_KEY: z.string().min(1),
    LIVEKIT_API_SECRET: z.string().min(1),
    LIVEKIT_SIP_ENDPOINT:z.string().min(1),
    LIVEKIT_SIP_PORT:z.string().min(1),
    LIVEKIT_URL: z.string().url(),
    LIVEKIT_WSS: z.string().min(1),
    LIVEKIT_AGENT_IMAGE: z.string(),
    LIVEKIT_DOCKER_HOST: z.string().optional(),
    LIVEKIT_DOCKER_PORT: z.string().optional(),
    LIVEKIT_K8S_NAMESPACE: z.string().min(1),
    LIVEKIT_K8S_IMAGE_REGISTRY: z.string().min(1),
  },
}

// 新增MILVUS配置
const milvusEnv = {
  server: {
    MILVUS_ENDPOINT: z.string().min(1),
    MILVUS_TOKEN: z.string().min(1),
  },
}

// 新增RAG配置
const ragEnv = {
  server: {
    RAG_SERVICE_URL: z.string().min(1),
    RAG_API_KEY: z.string().min(1),
    RAG_MODEL: z.string().min(1),
  },
}

// 新增FreeSWITCH配置
const freeswitchEnv = {
  server: {
    FREESWITCH_ENDPOINT: z.string().min(1),
    FREESWITCH_SIP_PORT: z.coerce.number(),
    FREESWITCH_ESL_PORT: z.coerce.number(),
  },
}

export const env = createEnv({
  server: {
    ...baseEnv.server,
    ...smtpEnv.server,
    ...s3Env.server,
    ...ossEnv.server,
    ...alismsEnv.server,
    ...livekitEnv.server,
    ...milvusEnv.server,
    ...ragEnv.server,
    ...freeswitchEnv.server,
    ...redisEmv.server
  },
  client: {
    ...baseEnv.client,
    ...smtpEnv.client,
  },
  experimental__runtimeEnv: {
    ...baseEnv.runtimeEnv,
    ...smtpEnv.runtimeEnv,
  },
  // 保持其他配置不变
  skipValidation:
    process.env.SKIP_ENV_CHECK === 'true' ||
    (typeof window !== 'undefined' && window.__ENV === undefined),
  onValidationError(error) {
    console.error(
      '❌ Invalid environment variables:',
      error.flatten().fieldErrors
    )
    throw new Error(
      `Invalid environment variables: ${JSON.stringify(
        error.flatten().fieldErrors
      )}`
    )
  },
  onInvalidAccess: (variable: string) => {
    throw new Error(
      `❌ Attempted to access a server-side environment variable on the client: ${variable}`
    )
  },
})
